import { defineMessages } from 'react-intl';

/**
 * Backoffice pages that live under a tool but are not tools themselves. The nav shows the parent
 * tool as current on these, and the shell renders a breadcrumb (All tools > parent > this page).
 * Paths are locale-less, like BACKOFFICE_TOOL_ROUTES.
 */
export type BackofficeSubpage = {
  path: string;
  /** Path of the tool in BACKOFFICE_TOOL_ROUTES that owns this page. */
  parentPath: string;
  labelKey: string;
  defaultLabel: string;
};

export const BACKOFFICE_SUBPAGES: BackofficeSubpage[] = [
  {
    path: '/backoffice/business-development/monetisation-plan',
    parentPath: '/backoffice/business-development',
    labelKey: 'backoffice.subpage.monetisationPlan',
    defaultLabel: 'Monetisation plan',
  },
  {
    path: '/backoffice/business-development/hiring-signals-market-entry',
    parentPath: '/backoffice/business-development',
    labelKey: 'backoffice.subpage.hiringSignals',
    defaultLabel: 'Hiring signals market entry',
  },
  // Deliberately not a nav tool: inbound feedback is managed from the Outreach CRM's Inbox tab.
  // The page still works for old links and shows a banner pointing there.
  {
    path: '/backoffice/dashboard/feedback',
    parentPath: '/backoffice/outreach-crm',
    labelKey: 'backoffice.subpage.feedback',
    defaultLabel: 'Feedback (legacy)',
  },
];

/**
 * Extraction-only block, same pattern as `backofficeToolMessages` in backoffice-tool-routes.tsx:
 * the labels above are looked up by dynamic id, which `extract:i18n` cannot see. Keep the ids and
 * English text in sync with the list above.
 */
export const backofficeSubpageMessages = defineMessages({
  monetisationPlan: {
    id: 'backoffice.subpage.monetisationPlan',
    defaultMessage: 'Monetisation plan',
    description: 'Breadcrumb label for the Monetisation plan page under Business Development in the backoffice',
  },
  hiringSignals: {
    id: 'backoffice.subpage.hiringSignals',
    defaultMessage: 'Hiring signals market entry',
    description: 'Breadcrumb label for the Hiring signals market entry page under Business Development in the backoffice',
  },
  feedback: {
    id: 'backoffice.subpage.feedback',
    defaultMessage: 'Feedback (legacy)',
    description: 'Breadcrumb label for the old standalone Feedback page in the backoffice, now superseded by the Outreach CRM inbox',
  },
});
